/**
 * Cache Storage API wrapper for static assets and small content files.
 * Used for assets < 50MB (images, small videos, JS, CSS, HTML).
 */

import { CONTENT_CACHE_NAME, STATIC_CACHE_NAME } from './types';

/**
 * Store a response in the content cache, keyed by URL.
 */
export async function cacheAsset(url: string, response: Response): Promise<void> {
  const cache = await caches.open(CONTENT_CACHE_NAME);
  await cache.put(url, response);
}

/**
 * Retrieve a cached response by URL from the content cache.
 */
export async function getCachedAsset(url: string): Promise<Response | undefined> {
  const cache = await caches.open(CONTENT_CACHE_NAME);
  return cache.match(url);
}

/**
 * Check if an asset is already in the content cache.
 */
export async function isAssetCached(url: string): Promise<boolean> {
  const cached = await getCachedAsset(url);
  return !!cached;
}

/**
 * Resolve a cached asset to a fresh blob URL for playback.
 *
 * Matches on the URL path ignoring the query string: player asset URLs are S3
 * presigned URLs that get re-signed on every manifest refresh, so an exact-URL
 * match would miss the entry cached under the previous signature. Serving a
 * blob URL also removes any dependency on the Service Worker intercepting the
 * request (it skips cross-origin S3 URLs) or on the presigned URL still being
 * valid (its STS token expires well before the manifest window).
 *
 * Returns null when the asset isn't in the content cache.
 */
export async function getCachedAssetBlobUrl(url: string): Promise<string | null> {
  const cache = await caches.open(CONTENT_CACHE_NAME);
  const cached =
    (await cache.match(url)) ?? (await cache.match(url, { ignoreSearch: true }));
  if (!cached) return null;
  const blob = await cached.blob();
  return URL.createObjectURL(blob);
}

/**
 * Delete an asset from the content cache.
 */
export async function deleteCachedAsset(url: string): Promise<boolean> {
  const cache = await caches.open(CONTENT_CACHE_NAME);
  return cache.delete(url);
}

/**
 * Get all URLs currently in the content cache.
 */
export async function getCachedAssetUrls(): Promise<string[]> {
  const cache = await caches.open(CONTENT_CACHE_NAME);
  const keys = await cache.keys();
  return keys.map((req) => req.url);
}

/**
 * Cache a static app asset (JS, CSS, HTML) during Service Worker install.
 */
export async function cacheStaticAssets(urls: string[]): Promise<void> {
  const cache = await caches.open(STATIC_CACHE_NAME);
  await cache.addAll(urls);
}

/**
 * Get storage usage estimate for the origin.
 */
export async function getStorageEstimate(): Promise<{
  usage: number;
  quota: number;
}> {
  if (!navigator.storage?.estimate) {
    return { usage: 0, quota: 0 };
  }
  const est = await navigator.storage.estimate();
  return {
    usage: est.usage ?? 0,
    quota: est.quota ?? 0,
  };
}
